import {
  faUpload,
  faUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ModalConfirmV2, ModalSizeVariants } from '@labrav/react-components'
import { Select, FormControl, InputLabel, MenuItem } from '@mui/material'
import { useMemo, useState } from 'react'
import { useStyles } from './CreateMeteringFormatSelector.styles'
import CreateMeteringFileUploader from '../CreateMeteringFileUploader/CreateMeteringFileUploader'
import { CloudAccountChangeV2, CloudAccountChangeParams } from '@labrav/common'
import {
  useCloudAccounts,
  getSelectedAccountIdWithCloud,
  extractAccountIdFromCombinedId,
} from '@labrav/common'
import { useAccountId } from '@labrav/common'
import { useCurrentCloudProvider } from '@labrav/common'
import { useFlag } from '@labrav/flags'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from '../../../../../store/hooks'
import { setSelectedAccount } from '@labrav/common'
import { getProducts } from '../../../../../modules/productsListing/action'
import { setCloudMarketplaceProductListing } from '../../../../../modules/productListingFilter/action'
import { setSelectedProductOnListing } from '../../../../../modules/productsListing/action'
import { actionTypeWrapper } from '@labrav/common'
import { useFormikContext } from 'formik'

function getFileUploadTypeFromFormat(
  selectedFormat: string
): 'csv' | 'excel' | undefined {
  if (selectedFormat === 'CSV') return 'csv'
  if (selectedFormat === 'MS Excel file') return 'excel'
  return undefined
}

export default function CreateMeteringFormatSelector({
  showModal,
  updateModalVisibility,
}: {
  showModal: boolean
  updateModalVisibility: () => void
}) {
  const { classes } = useStyles()
  const [selectedFormat, setSelectedFormat] = useState<string>('')

  // Formik fallback
  const formik = useFormikContext?.() as any
  const formikSetFieldValue = formik?.setFieldValue

  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId = accountIdFromHook || ''
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')
  const { flag: flyoutAzureEnabledMetering } = useFlag(
    'azureFlyoutUsageMetering'
  )

  const marketplaceCloudAccountsLocal = useMemo(
    () =>
      baseMarketplaceCloudAccounts.map(account => ({
        ...account,
        disabled:
          account.provider === 'GCP' ||
          (account.provider === 'AZURE' && !flyoutAzureEnabledMetering),
      })),
    [baseMarketplaceCloudAccounts, flyoutAzureEnabledMetering]
  )

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    selectedCloudTypeProductListing || 'AWS'
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const meteringFileUploadType = getFileUploadTypeFromFormat(selectedFormat)

  const handleCloudAccountChange = async (
    params: CloudAccountChangeParams,
    setFieldValue?: (field: string, value: any) => void
  ) => {
    // Use provided setter (Formik) when present
    const setter = setFieldValue || formikSetFieldValue || (() => {})

    // Extract original accountId from the combined format
    const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
    const { cloud } = params

    try {
      // Set selected cloud to the account's cloud (allow Azure uploads too)
      setter('selectedCloud', cloud)
      setter('selectedProduct', { label: '', key: '' })

      const cloudPath = cloud.toLowerCase()
      const pathname = `/flyout/${cloudPath}/${originalAccountId}/usage-metering`
      navigate(pathname, { replace: true })

      await dispatch(
        setSelectedAccount(originalAccountId, 'flyout', cloud as any)
      )
      await dispatch(setCloudMarketplaceProductListing(cloud))
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(getProducts(originalAccountId, cloud))
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Cloud account change failed', e)
    }
  }

  return (
    <div data-testId="createMetering-meteringFormatSelectorModal">
      <ModalConfirmV2
        title="Upload metering record"
        icon={<FontAwesomeIcon style={{ height: '16px' }} icon={faUpload} />}
        content={
          <div>
            <div className={classes.container}>
              <CloudAccountChangeV2
                cloudAccounts={marketplaceCloudAccountsLocal}
                selectedAccountId={selectedAccountIdWithCloud}
                onChange={params =>
                  handleCloudAccountChange(params, formikSetFieldValue)
                }
                width={171}
                size="medium"
              />
              <div style={{ flex: 1 }}>
                <FormatSelectorContent
                  selectedFormat={selectedFormat}
                  setSelectedFormat={setSelectedFormat}
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              {meteringFileUploadType ? (
                <CreateMeteringFileUploader
                  updateModalVisibility={updateModalVisibility}
                  fileUploadType={meteringFileUploadType}
                />
              ) : null}
            </div>
          </div>
        }
        open={showModal}
        hideAcceptButton
        hideCancelButton
        onAccept={() => void 0}
        onDecline={() => updateModalVisibility()}
        variant={ModalSizeVariants.large}
      />
    </div>
  )
}

const FormatSelectorContent = ({
  selectedFormat,
  setSelectedFormat,
}: {
  selectedFormat: string
  setSelectedFormat: any
}) => {
  const { classes } = useStyles()

  const handleClick = (e: any) => {
    e.preventDefault()
    if (e.target.value === 'API') {
      window.open(
        'https://docs.labra.io/reference/post_metering-metering-records'
      )
      setSelectedFormat('')
      return
    }
    setSelectedFormat(e.target.value as string)
  }
  return (
    <FormControl fullWidth variant="outlined" className={classes.form}>
      <InputLabel id="format-selector">Select format</InputLabel>
      <Select
        variant="outlined"
        labelId="format-selector"
        id="format-selector"
        value={selectedFormat}
        label="Select format"
        onChange={handleClick}
      >
        <MenuItem value="CSV">CSV file</MenuItem>
        <MenuItem value="MS Excel file">MS Excel file</MenuItem>
        <MenuItem value="API" className={classes.hyperlink}>
          <div className={classes.apiWrapper}>
            <a
              className={classes.hyperlink}
              href="https://docs.labra.io/reference/post_metering-metering-records"
              target="_blank"
              rel="noreferrer"
            >
              USING API
            </a>

            <div className={classes.iconContainer}>
              <FontAwesomeIcon icon={faUpRightFromSquare} color="#1942C5" />
            </div>
          </div>
        </MenuItem>
      </Select>
    </FormControl>
  )
}
